<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useForm } from "vee-validate";
import { useErrors } from "@/ts/utils/api-functions";
import * as Yup from "yup";
import FormInput from "@/ts/components/form/input.vue";
import FormFile from "@/ts/components/form/file.vue";
import Submit from "@/ts/components/form/submit.vue";
import axios from "axios";
import { checkIfFilesAreTooBig } from "@/ts/utils/file-validation";
import { useReCaptcha } from "vue-recaptcha-v3";

const apiErrors = useErrors();
const success = ref(false);
const loading = ref(false);
const height = ref(0);
const component = ref<HTMLDivElement>();
const reCaptcha = useReCaptcha();

interface Props {
    subject?: string;
    class?: string;
}

const props = defineProps<Props>();

const { handleSubmit, isSubmitting } = useForm({
    initialValues: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        comment: "",
        subject: props.subject && props.subject != "" ? props.subject : "Nieuw bericht",
        files: [],
    },
    validationSchema: Yup.object({
        subject: Yup.string().required().label("Onderwerp"),
        first_name: Yup.string().required().label("Voornaam"),
        last_name: Yup.string().required().label("Achternaam"),
        email: Yup.string().email().required().label("E-mailadres"),
        phone: Yup.string()
            .matches(/(((\+|00)3[12][ ]?(?:\(0\)[ ]?)?)|0){1}(4(60|[789]\d)\/?(\s?\d{2}\.?){2}(\s?\d{2})|(\d\/?\s?\d{3}|\d{2}\/?\s?\d{2})(\.?\s?\d{2}){2})/, "Telefoonnummer is ongeldig")
            .required()
            .label("Telefoonnummer"),
        comment: Yup.string().required().label("Bericht"),
        files: Yup.array().test("is-big-file", "Totale bestandsgrootte mag maximum 10MB zijn", checkIfFilesAreTooBig),
    }),
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        //formData.append("g-recaptcha-response", token);
        await axios.post("/api/contact", values);
        success.value = true;
        height.value = 0;
        setTimeout(() => {
            resetForm();
        }, 500);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

onMounted(() => {
    height.value = component.value!.offsetHeight;
});
</script>

<template>
    <div class="flex transition-all duration-500 ease-in-out" :style="`min-height: ${height}px`" ref="component" :class="class">
        <transition-group name="fade-absolute" tag="div" class="relative flex flex-1 w-full h-full">
            <form key="form" v-if="!success" @submit="onSubmit" class="grid flex-1 w-full grid-cols-2 gap-6">
                <form-input class="md:col-span-1 col-span-2" name="first_name" type="text" label="Voornaam" placeholder="Voornaam"></form-input>
                <form-input class="md:col-span-1 col-span-2" name="last_name" type="text" label="Achternaam" placeholder="Achternaam"></form-input>
                <form-input class="md:col-span-1 col-span-2" name="email" type="email" label="E-mailadres" placeholder="E-mailadres"></form-input>
                <form-input class="md:col-span-1 col-span-2" name="phone" type="text" label="Telefoonnummer" placeholder="Telefoonnummer"></form-input>
                <form-input class="col-span-2" name="comment" type="textarea" label="Bericht" placeholder="Bericht"></form-input>
                <form-file class="col-span-2" name="files" :multiple="true"></form-file>
                <component :is="apiErrors.render()" :data="apiErrors.get()"></component>
                <p class="col-span-2 text-center">
                    Als u op verzenden klikt gaat u akkoord met ons
                    <a href="https://www.iubenda.com/privacy-policy/39286583" class="text-pink hover:no-underline iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe underline" title="Privacybeleid ">privacybeleid</a>
                </p>
                <div class="flex justify-center col-span-2">
                    <submit class="btn btn-pink" :disabled="success" :loading="isSubmitting || loading">Verzenden</submit>
                </div>
                <p class="text-12 col-span-2 mt-10 text-center text-gray-500">
                    Deze website is beschermd met reCAPTCHA, zie hiervoor de
                    <a class="text-pink hover:no-underline underline" href="https://policies.google.com/privacy" rel="noopener" target="_blank">privacyverklaring</a>
                    en
                    <a class="text-pink hover:no-underline underline" href="https://policies.google.com/terms" rel="noopener" target="_blank">servicevoorwaarden</a>
                    van Google.
                </p>
            </form>
            <div key="success" class="flex items-center justify-center flex-1 w-full" v-else>
                <p class="text-center">Bedankt! We hebben uw bericht goed ontvangen. We nemen zo snel mogelijk contact met u op.</p>
            </div>
        </transition-group>
    </div>
</template>
